import { Component, Input, ChangeDetectionStrategy, OnDestroy, OnInit, forwardRef, ChangeDetectorRef, InjectionToken, Inject } from '@angular/core';
import { FormControl, FormGroup, ControlValueAccessor, NG_VALUE_ACCESSOR, ValidatorFn, ValidationErrors, AbstractControl, Validators } from '@angular/forms';
import { ReasonsStorage } from './reasons-storage';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { isString } from 'lodash';
import { maxLengthInBytes, maxBytesError } from '../shared/validators';

export const REASONS_STORAGE_TOKEN = new InjectionToken<ReasonsStorage>('REASONS_STORAGE');

interface ReasonFormGroupValues {
    standardReason: string | null;
    customReason: string | null;
    isCheckedCustomReason: boolean;
}

type ReasonFormGroupControls = {
    [formControlName in keyof ReasonFormGroupValues]: FormControl;
};

@Component({
    selector: 'audit-trail-reason',
    template: require('./audit-trail-reason.component.html'),
    styles: [require('./audit-trail-reason.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AuditTrailReasonComponent),
            multi: true
        }
    ]
})
export class AuditTrailReasonComponent implements OnDestroy, OnInit, ControlValueAccessor {
    @Input()
    public formControl: FormControl;
    @Input()
    public required = true;
    @Input()
    public maxByteslength = 250;
    @Input()
    public set onlyCustomReason(value: boolean) {
        this.isCheckedCustomReason.setValue(value);
        this.isEnabledCustomReason = value;
    }
    public isEnabledCustomReason: boolean;
    public reasons$: Observable<string[]>;
    public auditTrailReasonsStorage: ReasonsStorage;
    public readonly isCheckedCustomReason = new FormControl(false);
    public readonly standardReason = new FormControl(null);
    public readonly customReason = new FormControl(null);

    private subscription: Subscription;
    private readonly reasonFormGroup: FormGroup;
    private readonly hasOnlyWhiteSpaces = 'hasOnlyWhiteSpaces';

    constructor(
        @Inject(REASONS_STORAGE_TOKEN)auditTrailReasonsStorage: ReasonsStorage,
        readonly changeDetectorRef: ChangeDetectorRef
    ) {
        this.reasons$ = auditTrailReasonsStorage.all();
        const controls: ReasonFormGroupControls = {
            'standardReason': this.standardReason,
            'customReason': this.customReason,
            'isCheckedCustomReason': this.isCheckedCustomReason
        };

        this.reasonFormGroup = new FormGroup(controls);
    }

    public ngOnInit() {
        if (!this.formControl) {
            throw new Error('formControl should be passed');
        }

        if (this.required) {
            this.formControl.setValidators([Validators.required, maxLengthInBytes(this.maxByteslength), this.hasOnlyWhiteSpacesValidation()]);
        }

        this.observeReasonValues();
    }

    public ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public onChange = (reason: string) => { };
    public onTouched = () => { };

    public writeValue(reason: string) {
        this.onChange(reason);
    }

    public registerOnChange(fn: (reason: string) => {}) {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => {}) {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.reasonFormGroup.disable();
        } else {
            this.reasonFormGroup.enable();
        }
        this.changeDetectorRef.markForCheck();
    }

    private observeReasonValues() {
        const valueChanges = this.reasonFormGroup.valueChanges as Observable<ReasonFormGroupValues>;

        this.subscription = valueChanges.pipe(
            map(values => values.isCheckedCustomReason ? values.customReason : values.standardReason)
        ).subscribe(reason => {
            this.formControl.setValue(reason);
            this.onChange(reason);
            this.onTouched();
        });

        // emit initial valueChanges
        this.reasonFormGroup.updateValueAndValidity();
    }

    public get reasonErrorMessage() {
        if (this.formControl.hasError('required') || this.formControl.hasError(this.hasOnlyWhiteSpaces)) {
            return 'This field is required';
        }
        if (this.formControl.hasError(maxBytesError)) {
            return 'Value is too long';
        }
        return '';
    }

    private hasOnlyWhiteSpacesValidation(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return isString(control.value) && control.value.trim().length === 0
                ? { [this.hasOnlyWhiteSpaces]: true }
                : null;
        };
    }
}
